import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { get } from "lodash";
import { AccessType } from "@quintype/components";
import "./index.m.css";
import { requestAccessTypeToken, createSubscriptionSession } from "../subscription-page/auth-client.js";
import { GlobalSubscriptionSyncer } from "../../helper/GlobalSubscriptionSyncer";
import { OfferBannerWrapper } from "../../atoms/offer-banner/offer-banner-wrapper";
import { HeaderSection } from "./header-section";
import { SupportStrip } from "./support-strip";
import { PlanSection } from "./plan-section";
import { WhyChooseCarousel } from "./why-choose-carousel";
import { SubscriberBand } from "./subscriber-band";
import { TestimonialsSection } from "./testimonials-carousel";
import { FooterSection } from "./footer-section";
import { FaqSection } from "./faq-section";
import { ReviewPage } from "./review-page";
import { SuccessPage } from "./success-page";
import { FailurePage } from "./failure-page";
import { UpgradeModal } from "./upgrade-modal";
import { DowngradeModal } from "./downgrade-modal";
import { RenewalModal } from "../../molecules/renewal-modal";
import { AutoModal } from "./auto-modal";
import { AutoRenewelModal } from "./autorenewel-modal";
import { OnboardModal } from "./onboard-modal";
import { Snackbar } from "../../common/snackbar";
import { SpinnerLoader } from "../../common/loader";
import { setIsLogin, setUserDetails } from "../../store/actions";
import { useSubscriptionRetry } from "../../atoms/my-account/my-subscription/useSubscriptionRetry";
import { getUserDetails } from "../../helper/utils";
import { isSubscriptionActive } from "./utils/subscription-state";

const REDIRECT_URL_QUERY_PARAM = "redirect_url";
const EPAPER_LANDING_ORIGIN = "https://epaper.prajavani.net";
const PREMIUM_LISTING_URL = "/premium";
const PAYMENT_PROCESSING_STATE_KEY = "subscribe_payment_processing_state";
const SHOW_SUPPORT_STRIP_ON_REDIRECT_KEY = "show_support_strip_on_redirect";

const isHttpUrl = (value) => /^https?:\/\//i.test(value || "");

const getValidatedRedirectTarget = (value) => {
  if (!value || typeof value !== "string") return "";

  if (value.startsWith("/") && !value.startsWith("/subscribe")) {
    return value;
  }

  if (!isHttpUrl(value)) return "";

  try {
    const url = new URL(value);
    if (typeof window !== "undefined" && url.origin === window.location.origin && !url.pathname.startsWith("/subscribe")) {
      return `${url.pathname}${url.search}${url.hash}`;
    }

    if (url.origin === EPAPER_LANDING_ORIGIN || url.origin === "https://epaper-dev.prajavani.net") {
      return url.toString();
    }
  } catch (e) {
    return "";
  }

  return "";
};

const resolveRedirectUrl = () => {
  if (typeof window === "undefined") return PREMIUM_LISTING_URL;

  const params = new URLSearchParams(window.location.search);
  const redirectFromQuery = params.get(REDIRECT_URL_QUERY_PARAM) || "";
  const validatedTarget = getValidatedRedirectTarget(redirectFromQuery);
  if (validatedTarget) return validatedTarget;

  return PREMIUM_LISTING_URL;
};

const optimizeImageUrl = (url, width, quality) => {
  if (!url || typeof url !== "string") return url;
  try {
    const u = new URL(url);
    if (width) u.searchParams.set("w", width);
    if (quality) u.searchParams.set("q", quality);
    u.searchParams.set("auto", "format,compress");
    return u.toString();
  } catch (e) {
    const sep = url?.includes("?") ? "&" : "?";
    return `${url}${sep}w=${width}${quality ? `&q=${quality}` : ""}&auto=format,compress`;
  }
};

export const SubscribePageInner = ({ data = {}, initAccessType, getSubscription, getSubscriptionForUser, cancelSubscription, ...props }) => {
  const dispatch = useDispatch();
  const [plans, setPlans] = useState([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const userdetails = useSelector((state) => get(state, ["user", "user"], {}));
  console.log("[SubscribePageInner] userdetails from redux:", userdetails);
  const isUserLoginState = useSelector((state) => get(state, ["isLogin"], false));
  const hasUserDetails = !!(
    userdetails?.userData?.id ||
    userdetails?.id ||
    userdetails?.userData?.emails?.[0] ||
    userdetails?.email
  );
  const isUserLogin = isUserLoginState || hasUserDetails;
  console.log("isUserLoginState", {isUserLoginState, hasUserDetails, isUserLogin});

  useEffect(() => {
    if (hasUserDetails && !isUserLoginState) {
      console.log("setting is login true now");
      dispatch(setIsLogin(true));
    }
  }, [hasUserDetails, isUserLoginState, dispatch]);
  const qt = useSelector((state) => get(state, ["qt"], {}));

  const [view, setView] = useState(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const q = params.get("q");
      if (q === "success") return "success";
      if (q === "failure") return "failure";
      if (q === "review") return "review";
      return "subscribe";
    }

    try {
      const serverUrl = (qt && qt.currentPath) ? qt.currentPath : (props && props.location && props.location.search) ? props.location.search : "";
      if (serverUrl?.includes("q=review")) return "review";
      if (serverUrl?.includes("q=success")) return "success";
      if (serverUrl?.includes("q=failure")) return "failure";
      const queryParams = qt?.req?.query || qt?.currentRoute?.queryParams;
      if (queryParams && queryParams.q) {
        if (["success", "failure", "review"]?.includes(queryParams.q)) return queryParams.q;
      }
    } catch (e) { }

    return "subscribe";
  });
  const [popupType, setPopupType] = useState(null);
  const [isPlanChangeLoading, setIsPlanChangeLoading] = useState(false);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [isPaymentCompleted, setIsPaymentCompleted] = useState(false);
  const [showPaymentProcessingBanner, setShowPaymentProcessingBanner] = useState(false);
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const [isSubscriptionsLoaded, setIsSubscriptionsLoaded] = useState(false);
  const [planChangeData, setPlanChangeData] = useState(null);
  const [justLoggedIn, setJustLoggedIn] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.search?.includes("access_token")) {
      setJustLoggedIn(true);
    }
  }, []);

  const isInit = useRef(false);
  const paymentProcessingActiveRef = useRef(false);
  const isMountedRef = useRef(true);
  const { start: startSubscriptionRetry } = useSubscriptionRetry();

  const isLoggedIn = isUserLogin;
  const userData = userdetails;

  const fetchSubscription = useCallback(async () => {
    if (typeof getSubscriptionForUser !== "function") return [];
    console.log("fetchSubscription called");
    const subs = await getSubscriptionForUser();
    console.log("getSubscriptionForUser result:", subs);
    const subscriptionList = Array.isArray(subs)
      ? subs
      : subs?.subscriptions || [];

    if (isMountedRef.current) {
      setUserSubscriptions(subscriptionList);
      setIsSubscriptionsLoaded(true);
    }

    return subscriptionList;
  }, [getSubscriptionForUser]);

  const setSubscriptionStatus = (value) => {
    if (!value) {
      setIsPaymentCompleted(false);
    }
  };

  const updateSubscribeQuery = (updates = {}, removeKeys = [], options = {}) => {
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);

    removeKeys.forEach((key) => params.delete(key));
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        params.delete(key);
        return;
      }
      params.set(key, String(value));
    });

    const newSearch = params.toString();
    const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ""}${window.location.hash || ""}`;
    if (options.history === "push") {
      window.history.pushState({ path: newUrl }, "", newUrl);
      return;
    }
    window.history.replaceState({ path: newUrl }, "", newUrl);
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get(REDIRECT_URL_QUERY_PARAM)) return;

      if (!document.referrer) return;

      const referrerUrl = new URL(document.referrer);
      let redirectUrl = "";

      if (referrerUrl.origin === window.location.origin && !referrerUrl.pathname.startsWith("/subscribe")) {
        redirectUrl = `${referrerUrl.pathname}${referrerUrl.search}${referrerUrl.hash}`;
      } else if (referrerUrl.origin === EPAPER_LANDING_ORIGIN) {
        redirectUrl = referrerUrl.toString();
      }

      if (!redirectUrl) return;

      updateSubscribeQuery({ [REDIRECT_URL_QUERY_PARAM]: redirectUrl });
    } catch (e) {
      // no-op: redirect flow should still work with default fallback
    }
  }, []);

  useEffect(() => {
    const search = window?.location?.search;
    if (!search?.includes("access_token") || isUserLogin) return;
    const raw = (search || "").replace(/\?/g, "&").replace("&", "?");
    const params = new URLSearchParams(raw);
    const at = params.get("access_token");
    const rt = params.get("refresh_token");

    if (at && rt) {
      createSubscriptionSession({ accessToken: at, refreshToken: rt })
        .then(u => {
          params.delete("access_token");
          window.history.replaceState(null, "", window.location.pathname + (params.toString() ? `?${params.toString()}` : "") + (window.location.hash || ""));
          dispatch(setIsLogin(true));
          dispatch(setUserDetails(u));
        })
        .catch(e => {
          console.error("[SubscribePageInner] Failed to create session from tokens:", e);
        });
    }
  }, [isUserLogin]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const q = params.get("q");
      if (q === "success") { setView("success"); setPopupType(null); }
      else if (q === "failure") { setView("failure"); setPopupType(null); }
      else if (q === "review") { setView("review"); setPopupType(null); }
      else if (q === "renewel" || q === "auto" || q === "autorenewel") { setView("subscribe"); setPopupType(q); }
      else { setView("subscribe"); setPopupType(null); }

      // Handle browser back/forward buttons
      const handlePopState = () => {
        const p = new URLSearchParams(window.location.search);
        const qParam = p.get("q");
        if (qParam === "success") { setView("success"); setPopupType(null); }
        else if (qParam === "failure") { setView("failure"); setPopupType(null); }
        else if (qParam === "review") { setView("review"); setPopupType(null); }
        else if (qParam === "renewel" || qParam === "auto" || qParam === "autorenewel") { setView("subscribe"); setPopupType(qParam); }
        else { setView("subscribe"); setPopupType(null); }
      };
      window.addEventListener("popstate", handlePopState);
      return () => window.removeEventListener("popstate", handlePopState);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && plans.length > 0) {
      const params = new URLSearchParams(window.location.search);
      const q = params.get("q");
      const pId = params.get("plan_id");
      if (q === "review" && pId) {
        const p = plans.find((pl) => pl.id.toString() === pId);
        if (p) setSelectedPlan(p);
      }
    }
  }, [plans]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo(0, 0);
    }
  }, [view]);
  useEffect(() => {
    if (view === "review") {
      if (!isUserLogin) {
        if (typeof window !== "undefined") {
          const params = new URLSearchParams(window.location.search);
          if (params.get("access_token") || window.localStorage.getItem("pending_plan_id")) {
            return;
          }
        }

        setView("subscribe");
        if (typeof window !== "undefined") {
          updateSubscribeQuery({ q: null, plan_id: null });
        }
      } else if (isSubscriptionsLoaded) {
        const hasActive = userSubscriptions?.some(isSubscriptionActive);
        if (hasActive) {
          setView("subscribe");
          if (typeof window !== "undefined") {
            updateSubscribeQuery({ q: null, plan_id: null });
          }
        }
      }
    }
  }, [view, isUserLogin, isSubscriptionsLoaded, userSubscriptions]);

  const ensureAT = async () => {
    if (window.AccessType?.setUser) return true;
    return new Promise(r => { let a = 0; const c = setInterval(() => { if (window.AccessType?.setUser || ++a > 100) { clearInterval(c); r(!!window.AccessType?.setUser); } }, 100); if (!isInit.current) { isInit.current = true; initAccessType(() => { if (window.AccessType?.setUser) { clearInterval(c); r(true); } }); } });
  };

  const syncUser = async () => {
    const isReady = await ensureAT();
    console.log("[SubscribePageInner] ensureAT result:", isReady);
    if (!isReady) return;

    let token;
    try {
      token = await requestAccessTypeToken("");
    } catch (e) {
      console.error("[SubscribePageInner] Failed to get AccessType token:", e);
      return;
    }

    const email = userdetails?.userData?.emails?.[0] || userdetails?.email;
    const phone = userdetails?.userData?.phone || userdetails?.phone || userdetails?.phoneNumbers?.[0] || userdetails?.mobile || userdetails?.userData?.phoneNumbers?.[0];

    try {
      console.log("[SubscribePageInner] Setting user in AccessType:", { email, phone, token });
      await window.AccessType.setUser({ emailAddress: email, mobileNumber: phone, accesstypeJwt: token });
    } catch (e) {
      console.error("[SubscribePageInner] window.AccessType.setUser failed:", e);
    }
  };

  useEffect(() => {
    let isMounted = true;
    isMountedRef.current = true;

    const loadUserSubscriptions = async () => {
      if (!isUserLogin || (!userdetails?.userData?.id && !userdetails?.id)) return;
      try {
        await syncUser();
        const subs = await getSubscriptionForUser();
        const sList = Array.isArray(subs) ? subs : (subs?.subscriptions || []);
        if (isMounted) {
          setUserSubscriptions(sList);
          setIsSubscriptionsLoaded(true);
          const active = sList.find(isSubscriptionActive);
          if (active && justLoggedIn && typeof window !== "undefined") {
            const urlParams = new URLSearchParams(window.location.search);
            const redirectUrl = urlParams.get("redirect_url") || "/";
            const targetUrl = new URL(redirectUrl, window.location.origin);
            targetUrl.searchParams.set("showSnackbar", "activeSub");
            window.location.replace(targetUrl.toString());
            return; // keep loader spinning
          } else if (active && typeof window !== "undefined") {
            setShowPaymentProcessingBanner(false);
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent("show-snackbar", { detail: { type: "info", message: "You already have an active Subscription!" } }));
            }, 100);
          }
          if (justLoggedIn) setJustLoggedIn(false); // remove loader
        }
      } catch (err) {
        if (isMounted) {
          console.error("[SubscribePageInner] Error loading user subscriptions:", err);
          setIsSubscriptionsLoaded(true);
          if (justLoggedIn) setJustLoggedIn(false); // remove loader on error
        }
      }
    };

    const fetchPlans = () => {
      if (typeof getSubscription !== "function") {
        console.warn("[SubscribePageInner] getSubscription is not a function:", typeof getSubscription);
        setIsLoadingPlans(false);
        return;
      }

      if (typeof getSubscriptionForUser !== "function") {
        console.warn("[SubscribePageInner] getSubscriptionForUser is not a function:", typeof getSubscriptionForUser);
      }

      getSubscription()
        .then(async (groups) => {
          if (!isMounted) return;

          if (!Array.isArray(groups)) {
            setTimeout(fetchPlans, 1000);
            return;
          }

          // Extract all plans from all groups
          let allPlans = [];
          groups.forEach(group => {
            if (group.subscription_plans && Array.isArray(group.subscription_plans)) {
              allPlans = [...allPlans, ...group.subscription_plans];
            }
          });

          let cc = qt?.countryCode || get(qt, ["config", "countryCode"]) || "";

          // CLOUDFLARE FALLBACK: If server thinks we are in India (or is missing data), double check with Cloudflare Edge
          if ((!cc || cc.toUpperCase() === "IN") && typeof window !== "undefined") {
            try {
              const res = await fetch("https://www.cloudflare.com/cdn-cgi/trace");
              const text = await res.text();
              if (!isMounted) return;

              const locMatch = text.match(/loc=(.*)/);
              if (locMatch && locMatch[1]) {
                const cfCountry = locMatch[1].trim();
                // If Cloudflare proves they are outside India, override the server's guess
                if (cfCountry && cfCountry.toUpperCase() !== "IN") {
                  cc = cfCountry;
                }
              }
            } catch (e) {
              console.warn("Cloudflare trace fallback failed:", e);
            }
          }

          const isOutsideIndia = cc ? cc.toUpperCase() !== "IN" : false;

          // Deduplicate plans by ID in case the same plan exists in multiple groups
          const visiblePlans = allPlans.filter((p) => {
            let visibility = "";
            let international = false;
            if (Array.isArray(p.custom_attributes)) {
              const vObj = p.custom_attributes.find(attr => attr.name?.toLowerCase() === "visibility");
              if (vObj) visibility = String(vObj.value).toLowerCase();
              const intObj = p.custom_attributes.find(attr => attr.name?.toLowerCase() === "international");
              if (intObj) international = String(intObj.value).toLowerCase() === "true";
            } else if (p.custom_attributes && typeof p.custom_attributes === 'object') {
              visibility = String(p.custom_attributes.visibility || p.custom_attributes.Visibility || "").toLowerCase();
              international = String(p.custom_attributes.international || p.custom_attributes.International || "").toLowerCase() === "true";
            }

            if (visibility !== "show") return false;

            if (international) {
              return isOutsideIndia;
            } else {
              return !isOutsideIndia;
            }
          });

          // Deduplicate plans by ID in case the same plan exists in multiple groups
          const uniqueVisiblePlans = visiblePlans.filter((plan, index, self) =>
            index === self.findIndex((t) => t.id === plan.id)
          ).sort((a, b) => {
            const getOrder = (p) => {
              if (Array.isArray(p.custom_attributes)) {
                const o = p.custom_attributes.find(attr => attr.name?.toLowerCase() === "plan_order");
                return o && !isNaN(parseInt(o.value)) ? parseInt(o.value) : 99;
              } else if (p.custom_attributes && typeof p.custom_attributes === 'object') {
                const o = p.custom_attributes.plan_order || p.custom_attributes.Plan_order;
                return o && !isNaN(parseInt(o)) ? parseInt(o) : 99;
              }
              return 99;
            };
            return getOrder(a) - getOrder(b);
          });

          setPlans(uniqueVisiblePlans);
          setIsLoadingPlans(false);
        })
        .catch((err) => {
          if (!isMounted) return;
          console.error("[SubscribePageInner] Error fetching plans:", err);
          setPlans([]);
          setIsLoadingPlans(false);
        });
    };

    fetchPlans();
    loadUserSubscriptions();

    return () => {
      isMounted = false;
      isMountedRef.current = false;
    };
  }, [getSubscription, getSubscriptionForUser, isUserLogin, userdetails]);

  useEffect(() => { if (isUserLogin && !window.AccessType) ensureAT(); }, [isUserLogin]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const style = document.createElement("style");
      style.id = "hide-izooto-global";
      style.innerHTML = `
        #izooto-optin, .izooto-optinWrap, #iz-bell, .iz-bell, .izooto-optin, [id^="izooto"], [class*="izooto"],
        #optin-wrap, .optin-wrap, [id^="iz-"], [class^="iz-"], [class*=" iz-"],
        #onesignal-bell-container, .onesignal-bell-launcher, [id^="onesignal"], [class*="onesignal"], [id^="OneSignal"] {
          display: none !important;
          visibility: hidden !important;
          opacity: 0 !important;
          pointer-events: none !important;
        }
      `;
      document.head.appendChild(style);
      return () => {
        const s = document.getElementById("hide-izooto-global");
        if (s) s.remove();
      };
    }
  }, []);

  const pageData = data.data || data;
  const {
    hero_section = {},
    why_choose_section = {},
    testimonial_section = {},
    faq_section = {},
    footer_section = {},
    subscriber_band = {}
  } = pageData;

  useEffect(() => {
    if (isUserLogin && plans.length > 0 && isSubscriptionsLoaded && typeof window !== "undefined") {
      const active = userSubscriptions.find(isSubscriptionActive);
      if (active) {
        window.localStorage.removeItem("pending_plan_id");
        return;
      }

      const pendingId = window.localStorage.getItem("pending_plan_id");
      if (pendingId) {
        const p = plans.find((pl) => pl.id.toString() === pendingId.toString());
        if (p) {
          window.localStorage.removeItem("pending_plan_id");
          handlePlanSelect(p);
        }
      }
    }
  }, [isUserLogin, plans, isSubscriptionsLoaded, userSubscriptions]);

  const startPaymentProcessingVerification = useCallback(
    (expectedPlanId) => {
      if (paymentProcessingActiveRef.current) {
        // Ensure state is persisted even if already active
        setShowPaymentProcessingBanner(true);
        return;
      }

      // Wait for auth to complete before starting retry logic
      if (!isLoggedIn || !userData) {
        return;
      }

      paymentProcessingActiveRef.current = true;

      // Show payment processing banner
      setShowPaymentProcessingBanner(true);

      // Show support strip on the next redirected page only for this flow
      if (typeof window !== "undefined") {
        sessionStorage.setItem(SHOW_SUPPORT_STRIP_ON_REDIRECT_KEY, "1");
      }

      // Get expected plan ID from parameter, or fallback to sessionStorage
      let planId = expectedPlanId || null;

      // Persist payment processing state
      if (typeof window !== "undefined") {
        sessionStorage.setItem(
          PAYMENT_PROCESSING_STATE_KEY,
          JSON.stringify({
            active: true,
            startedAt: Date.now(),
            expectedPlanId: planId,
          })
        );
      }
      console.log("Starting subscription retry with expected plan ID:", planId);
      startSubscriptionRetry({
        fetchSubscription,
        expectedPlanId: planId,

        onSuccess: (data) => {
          console.log("Subscription retry succeeded, clearing payment processing state and sending success email");
          paymentProcessingActiveRef.current = false;

          // Send success email
          try {
            if (data) {
              const selectedPlan = data;
              const email = userdetails?.userData?.emails?.[0] || userdetails?.email || "";
              const phone = userdetails?.userData?.phoneNumbers?.[0] || userdetails?.userData?.phone || userdetails?.phone || "";
              const name = userdetails?.metadata?.first_name || userdetails?.name || "";
              const paymentAmount = (selectedPlan.plan_amount_cents || 0) / 100;
              const currencySymbol = selectedPlan.price_currency === "INR" ? "₹" : selectedPlan.price_currency === "USD" ? "$" : "";

              if (email || phone) {
                fetch("/subscription-success-email", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    email,
                    phone,
                    name,
                    plan_name: selectedPlan.plan_name || "",
                    payment_amount: paymentAmount,
                    currencySymbol,
                    paymentCurrency: selectedPlan.price_currency || "",
                    end_timestamp: selectedPlan?.end_timestamp || "",
                    start_timestamp: selectedPlan?.created_at || "",
                    id: selectedPlan?.subscription_plan_id || ""
                  })
                }).catch((e) => {
                  console.error("[SubscribePageInner] Failed to send subscription success email:", e);
                });
              }
            }
          } catch (e) {
            console.warn("Failed to send success email:", e);
          }

          // Clear payment processing state
          if (typeof window !== "undefined") {
            sessionStorage.removeItem(PAYMENT_PROCESSING_STATE_KEY);
            sessionStorage.removeItem(SHOW_SUPPORT_STRIP_ON_REDIRECT_KEY);
          }

          setShowPaymentProcessingBanner(false);

        },

        onTimeout: async () => {
          console.warn("Subscription retry timed out, clearing payment processing state and refreshing subscription");
          paymentProcessingActiveRef.current = false;

          if (typeof window !== "undefined") {
            sessionStorage.removeItem(PAYMENT_PROCESSING_STATE_KEY);
            sessionStorage.removeItem("subscriptionData");
            sessionStorage.removeItem("isSubscribed");
            sessionStorage.setItem("user_paid_status", "unpaid");
            sessionStorage.removeItem(SHOW_SUPPORT_STRIP_ON_REDIRECT_KEY);
          }

          try {
            await fetchSubscription();
            setSubscriptionStatus(false);
          } catch (error) {
            console.error(
              "Failed to refresh subscription after timeout:",
              error
            );
          }

          setShowPaymentProcessingBanner(false);
        },
      });
    },
    [
      fetchSubscription,
      setShowPaymentProcessingBanner,
      startSubscriptionRetry,
      isLoggedIn,
      userData,
    ]
  );

  useEffect(() => {
    if (typeof window !== "undefined" && isLoggedIn && userData) {
      try {
        const stateStr = sessionStorage.getItem(PAYMENT_PROCESSING_STATE_KEY);
        if (stateStr) {
          const state = JSON.parse(stateStr);
          if (state && state.active) {
            startPaymentProcessingVerification(state.expectedPlanId);
          }
        }
      } catch (e) {
        console.warn("Failed to resume payment processing state:", e);
      }
    }
  }, [isLoggedIn, userData, startPaymentProcessingVerification]);

  const handlePlanSelect = (plan) => {
    if (window) {
      const { getAuthStatus } = require("../../helper/datalayer-utils");
      const { status, uuid } = getAuthStatus();
      const formattedStatus = status === "not_logged_in" ? "non_logged_in" : status;

      const durationUnit = plan.duration_unit || "year";
      const durationLength = plan.duration_length || 1;
      let planName = `${durationLength}-${durationUnit.toLowerCase().replace(/s$/, "")}`;
      if (planName === "1-month") planName = "monthly";

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "subscription_plan_selection",
        auth_status: formattedStatus,
        uuid: uuid,
        plan_name: plan.title || planName,
        plan_price: plan.price_cents ? plan.price_cents / 100 : "NA",
        currency: plan.price_currency || "INR"
      });
    }
    console.log("isUserLogin", isUserLogin);

    if (!isUserLogin) {
      if (typeof window !== "undefined") {
        window.localStorage.setItem("pending_plan_id", plan.id);

        const redirectUrl = new URL(window.location.href);
        redirectUrl.searchParams.delete("access_token");
        redirectUrl.searchParams.delete("refresh_token");
        redirectUrl.searchParams.set("q", "review");
        redirectUrl.searchParams.set("plan_id", plan.id);

        const ssoEndpoint = get(qt, ["config", "publisher-attributes", "sso_endpoint"]);
        const ssoServer = get(qt, ["config", "publisher-attributes", "sso_endpoint_server"]);

        setTimeout(() => {
          sessionStorage.setItem("sso_login_initiated_source", "plan_selection");
          window.localStorage.removeItem("last_logged_in_uuid");
          window.location.assign(`${ssoEndpoint || ssoServer}/auth?redirectUrl=${encodeURIComponent(redirectUrl.toString())}`);
        }, 300);
      }
      return;
    }
    // Check if user has an ACTIVE subscription
    const activeSubscriptionObj = userSubscriptions?.find(s => isSubscriptionActive(s));
    const hasActiveSubscription = !!activeSubscriptionObj;

    if (hasActiveSubscription) {
      const matchedSubscription = activeSubscriptionObj;
      let currentPlan = plans.find(p => p.id === matchedSubscription.subscription_plan_id);

      // If the plan is retired/hidden from the page, reconstruct it from the subscription data
      if (!currentPlan) {
        currentPlan = {
          id: matchedSubscription.subscription_plan_id,
          price_cents: matchedSubscription.plan_amount_cents || matchedSubscription.payment_amount_cents || 0
        };
      }

      if (currentPlan && currentPlan.id !== plan.id) {
        const currentPrice = currentPlan.price_cents ?? 0;
        const selectedPrice = plan.price_cents ?? 0;

        if (window) {
          const { getAuthStatus } = require("../../helper/datalayer-utils");
          const { uuid } = getAuthStatus();

          const formatPlanName = (p) => {
            const durationUnit = p?.duration_unit || "year";
            const durationLength = p?.duration_length || 1;
            let planName = `${durationLength}-${durationUnit.toLowerCase().replace(/s$/, "")}`;
            return planName === "1-month" ? "monthly" : (p?.title || planName);
          };

          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: "plan_change_initiated",
            uuid: uuid,
            from_plan: formatPlanName(currentPlan),
            to_plan: formatPlanName(plan),
            plan_price: plan.price_cents ? plan.price_cents / 100 : "NA",
            currency: plan.price_currency || "INR"
          });
        }

        if (selectedPrice > currentPrice) {
          setPlanChangeData({ currentPlan, selectedPlan: plan, changeType: "upgrade", matchedSubscription });
          setPopupType("upgrade");
          return;
        } else if (selectedPrice < currentPrice) {
          setPlanChangeData({ currentPlan, selectedPlan: plan, changeType: "downgrade", matchedSubscription });
          setPopupType("downgrade");
          return;
        }
      }
    }

    setSelectedPlan(plan);
    if (typeof window !== "undefined") {
      const currentParams = new URLSearchParams(window.location.search);
      if (currentParams.get("q") !== "review" || currentParams.get("plan_id") !== String(plan.id)) {
        updateSubscribeQuery({ q: "review", plan_id: plan.id });
      }
    }
    setView("review");
  };

  const handleBackFromReview = () => {
    if (typeof window !== "undefined") {
      updateSubscribeQuery({ q: null, plan_id: null });
    }
    setView("subscribe");
  };

  const handleReviewFromFailure = () => {
    if (typeof window !== "undefined") {
      updateSubscribeQuery({ q: "review", plan_id: selectedPlan?.id || null });
    }
    setView("review");
  };

  const handleRetryFromFailure = async () => {
    if (!selectedPlan) {
      handleReviewFromFailure();
      return;
    }

    if (window) {
      const { getAuthStatus } = require("../../helper/datalayer-utils");
      const { uuid } = getAuthStatus();

      const durationUnit = selectedPlan.duration_unit || "year";
      const durationLength = selectedPlan.duration_length || 1;
      let planName = `${durationLength}-${durationUnit.toLowerCase().replace(/s$/, "")}`;
      if (planName === "1-month") planName = "monthly";

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "retry_payment_click",
        uuid: uuid,
        plan_name: selectedPlan.title || planName,
        plan_price: selectedPlan.price_cents ? selectedPlan.price_cents / 100 : "NA",
        currency: selectedPlan.price_currency || "INR"
      });
    }

    await handlePay(selectedPlan);
  };

  const handleReturnHomeFromFailure = () => {
    if (typeof window !== "undefined") {
      const target = new URL(resolveRedirectUrl(), window.location.origin);
      window.location.replace(target.toString());
    }
  };

  const getGatewayError = (response) => {
    return (
      response?.error?.["payment.from_gateway"]?.[0] ||
      response?.error?.payment?.from_gateway?.[0] ||
      null
    );
  };

  const getPaymentErrorMessage = (response) => {
    return getGatewayError(response)?.message || response?.error?.message || "";
  };

  const handlePay = async (p) => {
    if (isPaymentProcessing || isPaymentCompleted) return;

    setIsPaymentProcessing(true);
    let isSuccess = false;
    const amountCents = p?.discounted_price_cents ?? p?.price_cents;
    const paymentAmount = amountCents != null ? amountCents / 100 : "";
    const currencySymbol = p?.price_currency === "INR" ? "₹" : p?.price_currency === "USD" ? "$" : "";
    const email = userdetails?.userData?.emails?.[0] || userdetails?.userData?.email || userdetails?.metadata?.email || userdetails?.email || "";
    const phone = userdetails?.userData?.phoneNumbers?.[0] || userdetails?.userData?.phone || userdetails?.metadata?.phone || userdetails?.phone || "";
    const name = userdetails?.metadata?.first_name || userdetails?.name || "";
    let res;
    try {
      if (typeof window !== "undefined" && window.AccessType?.setUser) {
        const token = await requestAccessTypeToken("");
        await window.AccessType.setUser({ emailAddress: email, mobileNumber: phone, accesstypeJwt: token }).catch(e => console.log("setUser soft fail", e));
        await new Promise(r => setTimeout(r, 1000));
      }
      const opts = await window.AccessType.getPaymentOptions(), buildP = (p) => ({ type: "standard", plan: { id: p.id, title: p.title, price_cents: p.price_cents }, payment: { payment_type: (p.recurring || p.subscription_type === "recurring" || p.metadata?.recurring) ? "razorpay_recurring" : "razorpay", amount_cents: p.discounted_price_cents ?? p.price_cents, amount_currency: p.price_currency }, subscriber: { email: email, emailAddress: email, phone: phone, mobileNumber: phone, name: name } });
      res = await (opts?.data || opts)?.razorpay?.proceed(buildP(p));

      if (res?.subscription) {
        isSuccess = true;
        setIsPaymentCompleted(true);
        if (typeof window !== "undefined") {
          updateSubscribeQuery({ q: "success", plan_id: null });
          let planName = p.title || "";
          if (planName === "1-month") planName = "monthly";

          const { uuid } = require("../../helper/datalayer-utils").getAuthStatus();

          let userType = "returning user";
          if (!userSubscriptions || userSubscriptions.length === 0) {
            userType = "new user";
          }

          const eventPayload = {
            event: "purchase",
            uuid: uuid,
            plan_name: planName,
            plan_price: paymentAmount,
            currency: p.price_currency || "INR",
            transaction_id: res.razorpay_payment_id || res.payment?.id || res.subscription?.id || "NA",
            redirection_url: resolveRedirectUrl(),
            user_type: userType
          };
          console.log("DataLayer Purchase Event:", eventPayload);

          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push(eventPayload);

          // Fix: Proactively invalidate the syncer cache and update the DataLayer
          const cacheKey = `subscription_synced_${uuid}`;
          sessionStorage.removeItem(cacheKey);
          sessionStorage.setItem(`active_plan_name_${uuid}`, planName);
          sessionStorage.setItem(`active_plan_price_${uuid}`, paymentAmount);
          const pushProps = require("../../helper/datalayer-utils").pushUserPropertiesUpdateToDataLayer;
          pushProps(userdetails, true, planName, paymentAmount);
        }
        setView("success");

        // Do not block UI transition on email delivery.
        if (email || phone) {
          fetch("/subscription-success-email", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email,
              phone,
              name,
              plan_name: p.title || "",
              currencySymbol: currencySymbol,
              payment_amount: paymentAmount,
              paymentCurrency: p.price_currency || "",
              end_timestamp: res?.subscription?.end_timestamp || "",
              start_timestamp: res?.subscription?.created_at || "",
              id: res?.subscription?.id || ""
            })
          }).catch((e) => {
            console.error("[SubscribePageInner] Failed to send subscription success email:", e);
          });
        }
      }
    } catch (error) {
      console.error("[SubscribePageInner] Payment flow failed with error:", error);
      const paymentFromGateway =
        error?.error?.["payment.from_gateway"] ||
        error?.["payment.from_gateway"] ||
        error?.payment?.from_gateway;
      const gatewayMessage = Array.isArray(paymentFromGateway)
        ? paymentFromGateway[0]?.message
        : paymentFromGateway?.message;
      // Check for plan amount mismatch error - can be present under payment.amount_cents
      const paymentAmountCentsError =
        error?.error?.["payment.amount_cents"] ||
        error?.["payment.amount_cents"] ||
        error?.payment?.amount_cents;
      const paymentAmountCentsMessage = Array.isArray(paymentAmountCentsError)
        ? paymentAmountCentsError[0]
        : paymentAmountCentsError;

      // Check for error message in various locations
      const errorMessage = error?.message || error?.error?.message || gatewayMessage || "";
      const isPlanAmountMismatchError =
        errorMessage?.includes("0 is not matching subscription plan amount") ||
        (typeof paymentAmountCentsMessage === "string" &&
          paymentAmountCentsMessage?.includes("0 is not matching subscription plan amount"));
      const isSubscriptionVerificationError =
        gatewayMessage === "Verification failed. Subscription neither active nor authenticated" ||
        gatewayMessage === "Subscription neither active or authenticated" ||
        isPlanAmountMismatchError;
      console.log('isSubscriptionVerificationError', isSubscriptionVerificationError);
      if (isSubscriptionVerificationError) {
        console.log("[SubscribePageInner] Detected subscription verification error, persisting payment processing state for background verification");

        // Persist payment processing state directly without starting timers (they won't survive the redirect)
        if (typeof window !== "undefined") {
          let planId = p?.id || null;
          sessionStorage.setItem(
            PAYMENT_PROCESSING_STATE_KEY,
            JSON.stringify({
              active: true,
              startedAt: Date.now(),
              expectedPlanId: planId,
            })
          );
          sessionStorage.setItem(SHOW_SUPPORT_STRIP_ON_REDIRECT_KEY, "1");

          window.dispatchEvent(new CustomEvent("show-snackbar", { detail: { type: "info", message: "We are verifying your payment and subscription, the process may take a few minutes." } }));

          const target = new URL(resolveRedirectUrl(), window.location.origin);
          window.location.replace(target.toString());
        }
      }

      const gatewayError = getGatewayError(error);
      const paymentErrorMessage = getPaymentErrorMessage(error);

      if ((email || phone) && !isSubscriptionVerificationError) {
        try {
          await fetch("/subscription-failure-email", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              email,
              phone,
              name,
              plan_name: p.title || "",
              currencySymbol: currencySymbol,
              payment_amount: paymentAmount,
              paymentCurrency: p.price_currency || "",
              id: gatewayError?.payload || res?.error?.id || "",
              payment_error: paymentErrorMessage
            })
          });
        } catch (e) {
          console.error("[SubscribePageInner] Failed to send subscription failure email:", e);
        }
      }
      if (typeof window !== "undefined") {
        let planName = p.title || "";
        if (planName === "1-month") planName = "monthly";
        const { uuid } = require("../../helper/datalayer-utils").getAuthStatus();

        const eventPayload = {
          event: "payment_failed",
          uuid: uuid,
          plan_name: planName,
          plan_price: paymentAmount,
          currency: p.price_currency || "INR",
          reference_id: gatewayError?.payload || res?.error?.id || error?.error?.metadata?.payment_id || error?.payment?.id || "NA"
        };
        console.log("DataLayer Payment Failed Event:", eventPayload);

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(eventPayload);

        updateSubscribeQuery({ q: "failure", plan_id: null }, [], { history: "push" });
      }
      setView("failure");
    } finally {
      if (!isSuccess) {
        setIsPaymentProcessing(false);
      }
    }
  };

  const handleCloseModal = () => {
    if (typeof window !== "undefined") {
      updateSubscribeQuery({ q: null, plan_id: null });
    }
    setPopupType(null);
    setPlanChangeData(null);
  };

  const handleConfirmPlanChange = async () => {
    if (!planChangeData) return;
    const email = userdetails?.userData?.emails?.[0] || userdetails?.email;
    const phone = userdetails?.userData?.phoneNumbers?.[0];
    console.log("[SubscribePageInner] Sending plan change request:", planChangeData, "email:", email, "phone:", phone);

    setIsPlanChangeLoading(true);
    try {
      const response = await fetch("/plan-change-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          phone,
          currentPlan: planChangeData.currentPlan,
          selectedPlan: planChangeData.selectedPlan,
          changeType: planChangeData.changeType,
          matchedSubscription: planChangeData.matchedSubscription || null,
        })
      });

      let isSuccess = response.success;
      if (response.success) {
        try {
          const result = await response.clone().json();
          if (typeof result?.success !== "undefined") {
            isSuccess = !!result.success;
          }
        } catch (e) {
          // Ignore JSON parse failures and rely on HTTP status.
        }
      }

      if (isSuccess && typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("show-snackbar", { detail: { type: "info", message: `Plan ${planChangeData.changeType} request sent` } })
        );
      }
    } catch (e) {
      console.error("[SubscribePageInner] Plan change request failed:", e);
    } finally {
      setIsPlanChangeLoading(false);
      handleCloseModal();
    }
  };

  if (justLoggedIn) {
    return <SpinnerLoader />;
  }

  const syncer = <GlobalSubscriptionSyncer />;

  if (view === "success") {
    return <>{syncer}<SuccessPage /></>;
  }

  if (view === "failure") {
    return (
      <>
        {syncer}
        <FailurePage
          onRetry={handleRetryFromFailure}
          onReview={handleReviewFromFailure}
          onReturnHome={handleReturnHomeFromFailure}
          isPaymentProcessing={isPaymentProcessing}
        />
      </>
    );
  }

  if (view === "review") {
    if (isUserLogin && !isSubscriptionsLoaded) {
      return <>{syncer}<SpinnerLoader /></>;
    } else if (selectedPlan) {
      return <>{syncer}<ReviewPage plan={selectedPlan} onBack={handleBackFromReview} onPay={handlePay} isPayDisabled={isPaymentProcessing || isPaymentCompleted} isPaying={isPaymentProcessing} /></>;
    } else if (isLoadingPlans) {
      return <>{syncer}<SpinnerLoader /></>;
    }
    // Fallback: If everything loaded and selectedPlan is still null,
    // it will just fall through and render the main subscribe page safely.
  }

  return (
    <div styleName="pageContainer">
      {syncer}
      {showPaymentProcessingBanner && (
        <SupportStrip message="We are verifying your payment. If this takes longer than expected, contact us at" isSubscribePage={true} />
      )}
      <div styleName="homeOfferBanner">
        <OfferBannerWrapper />
      </div>
      {/* 1. Hero Section */}
      <HeaderSection data={pageData} />

      {/* 2. Plan Section */}
      <PlanSection plans={plans} isLoadingPlans={isLoadingPlans} onPlanSelect={handlePlanSelect} userSubscriptions={userSubscriptions} isSubscriptionsLoaded={isSubscriptionsLoaded} isUserLogin={isUserLogin} showPaymentProcessingBanner={showPaymentProcessingBanner} />

      {/* 3. Why Choose Section */}
      <WhyChooseCarousel />

      {/* 3.5 Subscriber Band */}
      <SubscriberBand data={subscriber_band} />

      {/* 4. Testimonials Section */}
      <TestimonialsSection data={testimonial_section} />

      {/* 5. FAQ Section */}
      <FaqSection data={faq_section} />

      {/* 6. Footer Section */}
      <FooterSection />

      {/* 7. Modal Overlay */}
      {popupType === "upgrade" && <UpgradeModal onClose={handleCloseModal} onConfirm={handleConfirmPlanChange} isLoading={isPlanChangeLoading} />}
      {popupType === "downgrade" && <DowngradeModal onClose={handleCloseModal} onConfirm={handleConfirmPlanChange} isLoading={isPlanChangeLoading} userSubscriptions={userSubscriptions} />}
      {popupType === "renewel" && <RenewalModal onClose={handleCloseModal} />}
      {popupType === "auto" && <AutoModal onClose={handleCloseModal} />}
      {popupType === "autorenewel" && <AutoRenewelModal onClose={handleCloseModal} expiryDate={""} />}
      {popupType === "onboard" && <OnboardModal onClose={handleCloseModal} />}

      {/* Global Overlays */}
      <Snackbar />
    </div>
  );
};

export const SubscribePage = (props) => {
  // Explicitly passing accessTypeKey just in case Redux state is missing it
  const fallbackKey = "dr9394kutfXvSAuHxynXv4RQ";
  const dispatch = useDispatch();
  const userdetails = useSelector((state) => get(state, ["user", "user"], {}));
  const qt = useSelector((state) => get(state, ["qt"], {}));

  const email = userdetails?.userData?.emails?.[0] || userdetails?.email;
  const phone = userdetails?.userData?.phoneNumbers?.[0] || userdetails?.phone;

  useEffect(() => {
    if (typeof window === "undefined") return;

    const searchParams = new URLSearchParams(window.location.search);
    const access_token = searchParams.get("access_token");
    const refresh_token = searchParams.get("refresh_token");
    const userLocalStorage = localStorage.getItem("user");
    const stored_access_token = localStorage.getItem("access_token");
    const stored_refresh_token = localStorage.getItem("refresh_token");

    // Subscribe page does not render the site header, so we must hydrate auth here.
    if (stored_refresh_token && stored_access_token) {
      dispatch(setIsLogin(true));
      getUserDetails()
        .then((data) => {
          if (data && data.status !== 401) {
            dispatch(setUserDetails(data || {}));
            localStorage.setItem("user", JSON.stringify(data));
          }
        })
        .catch((err) => {
          console.error("[SubscribePage] Failed to fetch user details:", err);
        });
    }

    if (access_token && refresh_token) {
      // SSO callback tokens are handled in SubscribePageInner.
      return;
    }

    if (userLocalStorage && userLocalStorage !== "undefined" && userdetails?.userData === undefined) {
      try {
        const parsedUser = JSON.parse(userLocalStorage);
        if (parsedUser && typeof parsedUser === "object") {
          dispatch(setUserDetails(parsedUser));
          if (stored_access_token && stored_refresh_token) {
            dispatch(setIsLogin(true));
          }
        }
      } catch (error) {
        console.error("[SubscribePage] Failed to parse localStorage user:", error);
      }
    }
  }, [dispatch]);
  const ck = get(qt, ["config", "publisher-attributes", "subscription_setup"], {}), aKey = ck.accesstype_key || get(qt, ["config", "publisher-attributes", "accesstype_key"], "");
  return (
    <AccessType
      accessTypeKey={aKey}
      accessTypeBkIntegrationId={ck.accesstype_subauth || ""}
      enableAccesstype={true}
      email={email}
      phone={phone}
    >
      {accessTypeProps => {
        return <SubscribePageInner {...props} {...accessTypeProps} />
      }}
    </AccessType>
  );
};
